import { FORM_CONTROL_FOCUS_RING } from "../../lib/a11yFocus";
import { Helmet } from "react-helmet";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";
import PageTemplate from "../../components/BaseComponents/PageTemplate/PageTemplate";
import routes from "../../components/Router/routes";
import {  useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import useIsViewOnly from "../../hooks/useIsViewOnly";
import { Button, InlineAlert, LoadingSpinner } from "../../components/BaseComponents";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { formatDate, formatReportStatus, getApiErrorMessage, getCSRFToken } from "../../lib/utils";
import usePagination from "../../hooks/usePagination";
import useDataSort from "../../hooks/useDataSort";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import useCleanup from "../../hooks/useCleanup";
import ConfirmationModal from "../../components/BaseComponents/ConfirmationModal";
import useAddNotification from "../../hooks/useAddNotification";

function MyReports() {
    const [data, setData] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [deletionCandidate, setDeletionCandidate] = useState<any>();
    const [deleting, setDeleting] = useState(false);
    // #884: the now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE notify-only codes
    // (#857 item 6) previously had no catch on any of this page's API calls, so a rejection
    // produced an unhandled rejection with no visible feedback - a denied delete/re-baseline
    // left `deleting` (or the clicked radio) stuck, and a denied load left the table silently
    // stale/empty. Load and write failures use separate slots (mirroring create2.tsx's
    // loadError/saveError split) rather than one shared slot: an unrelated *successful*
    // background reload (e.g. triggered by a pagination/sort change) must not silently wipe a
    // still-true write-denial banner, which a single shared slot would do.
    const [loadError, setLoadError] = useState('');
    const [actionError, setActionError] = useState('');

    const navigate = useNavigate();
    const api = useApi();
    const isViewOnly = useIsViewOnly();
    const addNotification = useAddNotification();

    const onDelete = async (reportId: string) => {
        // Captured before the confirm dialog closes below, since a failed delete must still
        // clear `deletionCandidate` (same as the success path) rather than leaving the user
        // stuck on the confirmation dialog mid-delete.
        const candidate = deletionCandidate;
        setDeleting(true);
        setActionError('');
        try {
            await api.delete(`/secr/${reportId}`, {
                _csrf: await getCSRFToken(),
            });
        } catch (err) {
            // A denied/failed delete must not look successful - no notification, no reload.
            setActionError(getApiErrorMessage(err, 'Failed to delete this report. Please try again.'));
            setDeleting(false);
            setDeletionCandidate(null);
            return;
        }
        if(candidate) {
            addNotification({
                body: `Report ${formatDate(candidate.start_date)} - ${formatDate(candidate.end_date)} deleted`,
            })
        }
        setDeletionCandidate(null);
        setDeleting(false);

        loadData();
    }

    const loadData = useCleanup(() => {
        let cancelled = false;
        const run  = async () => {
            const qs = new URLSearchParams({
                ...pagination.params,
                ...dataSort.params,
            })

            const result = await api.get('secr/own?' + qs);

            if(cancelled) {
                return;
            }
            setData(result.data.reports);
            pagination.readMeta(result.data.meta);
            setLoadError('');
            setLoading(false);
        }
        run().catch((err) => {
            if(cancelled) {
                return;
            }
            setLoadError(getApiErrorMessage(err, 'Failed to load your reports. Please try again.'));
            setLoading(false);
        })
        return () => {
            cancelled = true;
        }
    });

    const pagination = usePagination();
    const dataSort = useDataSort('end_date', 'desc');

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.params, dataSort.params])

    if(loading || !data) {
        return <LoadingSpinner />
    }

    const columns: TableColumn[] = [
        {
            id: 'end_date',
            label: 'Name',
            renderer: (key, row) => {
                return (
                    <span>
                        { formatDate(row.start_date) }
                        {' - '}
                        { formatDate(row.end_date) }
                    </span>
                )
            },
            sortable: true,
        },
        {
            id: 'type',
            label: 'Created By',
            sortable: true,
                renderer: (key, row) => {
                const type = row[key];
                const createdBy = type === 'Accounts summary'
                    ? 'System generated'
                    : 'Manual input';
                return (
                    <span>
                        { createdBy}
                    </span>
                );
            }
        },
        {
            id: 'status',
            label: 'Status',
            sortable: true,
            renderer: (key, row) => <span>{formatReportStatus(row[key])}</span>,
        },
        {
            id: 'first_published',
            label: 'First published',
            renderer: (key, row) => <span>{formatDate(row[key])}</span>,
            sortable: true,
        },
        {
            id: 'publish_date',
            label: 'Last updated',
            renderer: (key, row) => <span>{row[key]  && formatDate(row[key])}</span>,
            sortable: true,
        },
        {
            id: 'actions',
            label: 'Actions',
            textAlign: 'center',
            renderer: (key, row) => {
                const editRoute = (row['type'] === 'Accounts summary' && row['accounting_period_id'])
                    ? generatePath(routes.companyCarbonAccounts.path, { accountingPeriodId: row['accounting_period_id'] })
                    : generatePath(routes.myReportsEdit.path, { reportId: row['id']});
                return (
                    <div className="space-x-2">
                        <Button requiresWrite type="Primary" icon="fa-solid fa-trash" hasLeadingIcon size="sm" isTableRowButton onClick={() => setDeletionCandidate(row)} />
                        <Button
                            requiresWrite
                            type="Primary"
                            icon="fa-solid fa-pen-to-square"
                            hasLeadingIcon
                            size="sm"
                            isTableRowButton
                            isLink
                            url={editRoute}
                        />
                    </div>
                )
            }
        },
        {
            id: 'baseline', 
            label: 'Baseline',
            textAlign: 'center',
            renderer: (key, row) => {
                const { is_baseline } = row;
                const onSetBaseline = async () => {
                    setActionError('');
                    try {
                        await api.post(
                            'secr/setBaseline',
                            {
                                reportId: row.id,
                                _csrf: await getCSRFToken(),
                            }
                        )
                    } catch (err) {
                        // A denied/failed re-baseline must not look successful - the radio
                        // selection is left as-is (no reload) and a visible failure is shown.
                        setActionError(getApiErrorMessage(err, 'Failed to update the baseline report. Please try again.'));
                        return;
                    }
                    loadData()
                }
                return (
                    <label className='flex items-center cursor-pointer'>
                        <input
                            type="radio"
                            className={`h-4 w-4 border-charcoal-300 text-algae-600 cursor-pointer ${FORM_CONTROL_FOCUS_RING}`}
                            checked={is_baseline}
                            disabled={isViewOnly}
                            onClick={is_baseline ? undefined : onSetBaseline}
                        />
                    </label>
                )
            }
        },
        {
            id: 'view', 
            label: 'View',
            textAlign: 'center',
            renderer: (key, row) => {
                const route = generatePath(routes.myReport.path, { reportId: row.id });
                return (
                    <Link to={route}>
                        <Button
                            type='Primary'
                            text='View'
                            size='sm'
                            hasTrailingIcon
                        />
                    </Link>
                )
            }
        },
    ]

    return(
        <PageTemplate testid='company-dashboard-main'>
            <Helmet>
                <title>{routes.myReports.name}</title>
            </Helmet>
            <PageHeadings title='My Reports'/>

            { actionError && (
                <InlineAlert type='error' className='mt-4' data-testid='my-reports-action-error'>{actionError}</InlineAlert>
            )}
            { loadError && (
                <InlineAlert type='error' className='mt-4' data-testid='my-reports-load-error'>{loadError}</InlineAlert>
            )}

			<Button
				requiresWrite
				type='Primary'
				text='Manually create report'
				size='sm'
				hasTrailingIcon
				icon='fa-solid fa-plus'
				onClick={() => {
					navigate(routes.myReportsCreate.path)
				}}
				additionalClass='mt-7 w-1/4'
			/>
            <div className="mt-8">
                <SimpleSortTable
                    columns={columns}
                    data={data}
                    defaultSorting={dataSort.defaultSorting}
                    onSortPress={dataSort.onChange}
                />
                <Pagination
                    pagination={pagination.pagination}
                    onPageChange={pagination.onPageChange}
                />
            </div>
            <ConfirmationModal
                open={!!deletionCandidate}
                onCancel={() => setDeletionCandidate(null)}
                onConfirm={() => deletionCandidate && onDelete(deletionCandidate.id)}
                title={deletionCandidate ? `Delete report for ${formatDate(deletionCandidate.start_date)} - ${formatDate(deletionCandidate.end_date)}` : ''}
            >
                { deletionCandidate && (
                    deleting ? (
                        <LoadingSpinner />
                    ) : (
                        <div className="mt-8 text-white">
                            Confirm deletion of report for period {formatDate(deletionCandidate.start_date)} - {formatDate(deletionCandidate.end_date)}
                        </div>
                    )
                )}
            </ConfirmationModal>
        </PageTemplate>
    )
}

export default MyReports;
